import { createContext, useReducer, ReactNode } from 'react';
import rootReducer, { initialState } from '../utils/rootReducer';
import { updateInput } from '../utils/helperFunctions';
import type {
  GlobalContextValue,
  UpdateInputArgs,
  Round,
  InputValue,
} from '../../types';

export const GlobalContext = createContext<GlobalContextValue>(
  {} as GlobalContextValue,
);

const isTeamTotalUpdate = (fieldToUpdate: string) => {
  return fieldToUpdate.includes('team') && fieldToUpdate.includes('Total');
};

const isActualUpdate = (fieldToUpdate: string) => {
  return fieldToUpdate.includes('Actual');
};

const buildTeamTotalRound = ({
  input,
  fieldToUpdate,
  currentRound,
}: UpdateInputArgs): Round => {
  const teamNumber = fieldToUpdate.includes('team1') ? 1 : 2;
  const teamField =
    teamNumber === 1 ? 'team1BidsAndActuals' : 'team2BidsAndActuals';

  const numInput = parseInt(input as string, 10);
  const individualValue = Math.floor(numInput / 2);
  const remainder = numInput % 2;
  const p1Value = individualValue + remainder;

  const autoGeneratedKey1 = teamNumber === 1 ? 'team1P1' : 'team2P1';
  const autoGeneratedKey2 = teamNumber === 1 ? 'team1P2' : 'team2P2';

  return {
    ...currentRound,
    [teamField]: {
      ...currentRound[teamField],
      p1Actual: String(p1Value) as InputValue,
      p2Actual: String(individualValue) as InputValue,
    },
    autoGeneratedActuals: {
      ...currentRound.autoGeneratedActuals,
      [autoGeneratedKey1]: true,
      [autoGeneratedKey2]: true,
    } as Round['autoGeneratedActuals'],
  };
};

const buildActualRound = (args: UpdateInputArgs): Round => {
  const { fieldToUpdate } = args;
  const teamNumber = fieldToUpdate.includes('team1') ? 1 : 2;
  const playerNumber = fieldToUpdate.includes('p1Actual') ? 'P1' : 'P2';
  const autoGeneratedKey = `team${teamNumber}${playerNumber}`;

  const updatedRound = updateInput(args);

  return {
    ...updatedRound,
    autoGeneratedActuals: {
      ...updatedRound.autoGeneratedActuals,
      [autoGeneratedKey]: false,
    } as Round['autoGeneratedActuals'],
  };
};

export const StateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(rootReducer, initialState);

  const setCurrentRound = (args: UpdateInputArgs) => {
    if (isTeamTotalUpdate(args.fieldToUpdate)) {
      dispatch({
        type: 'SET_CURRENT_ROUND',
        payload: { currentRound: buildTeamTotalRound(args) },
      });
    } else if (isActualUpdate(args.fieldToUpdate)) {
      dispatch({
        type: 'SET_CURRENT_ROUND',
        payload: { currentRound: buildActualRound(args) },
      });
    } else {
      dispatch({
        type: 'SET_CURRENT_ROUND',
        payload: { currentRound: updateInput(args) },
      });
    }
  };

  const resetCurrentRound = () => {
    dispatch({
      type: 'RESET_CURRENT_ROUND',
    });
  };

  const setRoundHistory = (newRoundHistory: Round[]) => {
    const clonedNewRoundHistory = [...newRoundHistory];
    dispatch({
      type: 'SET_ROUND_HISTORY',
      payload: {
        roundHistory: clonedNewRoundHistory,
      },
    });
  };

  const setFirstDealerOrder = (newFirstDealerOrder: string[]) => {
    const clonedNewFirstDealerOrder = [...newFirstDealerOrder];
    dispatch({
      type: 'SET_FIRST_DEALER_ORDER',
      payload: {
        firstDealerOrder: clonedNewFirstDealerOrder,
      },
    });
  };

  const setDealerOverride = (dealerOverride: string | null) => {
    dispatch({
      type: 'SET_DEALER_OVERRIDE',
      payload: {
        dealerOverride,
      },
    });
  };

  const resetRoundHistory = () => {
    dispatch({
      type: 'RESET_ROUND_HISTORY',
    });
  };

  const globalStore = {
    setCurrentRound,
    setRoundHistory,
    resetRoundHistory,
    resetCurrentRound,
    setFirstDealerOrder,
    setDealerOverride,
    firstDealerOrder: state.firstDealerOrder,
    currentRound: state.currentRound,
    roundHistory: state.roundHistory,
    isFirstGameAmongTeammates: state.isFirstGameAmongTeammates,
  };

  return (
    <GlobalContext.Provider value={globalStore}>
      {children}
    </GlobalContext.Provider>
  );
};
